import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { updateProjectDetails } from '../../apis/apiList/projectApi';
import { useMessage } from '../../context/MessageContext';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4
};

const statusOptions = ["Not Started", "Active", "pending approval", "Completed" ];

const EditProjectModal = ({ open, onClose, onSubmit, projectData, userDetails }) => {
  console.log('projectData', projectData);
  const [formData, setFormData] = useState({
    projectName: projectData.projectName || '',
    dueDate: new Date(projectData.dueDate) || '',
    status: projectData.status || '',
    approvals: projectData.approvals || {
      scriptsAndVisuals: false,
      voiceOver: false,
      video: false
    } 
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showMessage } = useMessage();

  useEffect(() => {
    if (projectData) {
      console.log('projectData in use Effect', projectData);
      setFormData({
        projectName: projectData.projectName || '',
        dueDate: projectData.dueDate ? projectData.dueDate.slice(0, 10) : '',
        status: projectData.status || '',
        approvals: {
          scriptsAndVisuals: projectData.approvals?.scriptsAndVisuals || false,
          voiceOver: projectData.approvals?.voiceOver || false,
          video: projectData.approvals?.video || false
        }
      });
    }
  }, [projectData]);

  const validate = () => {
    const newErrors = {};
    if (!formData.projectName.trim()) newErrors.projectName = 'Project name is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';
    if (!formData.status) newErrors.status = 'Status is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({
      ...prev,
      approvals: {
        ...prev.approvals,
        [field]: !prev.approvals[field]
      }
    }));
  };

  const handleUpdate = async () => {
    if (validate()) {
      setIsSubmitting(true);
      try {
        const response = await updateProjectDetails(projectData._id, formData, {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": userDetails.jwtToken,
          },
        });

        onSubmit(response.updateDeatils);
        setIsSubmitting(false);
        onClose();
        showMessage('Project updated successfully', 'success');
      } catch (error) {
        console.error('Error updating project:', error);
        showMessage(error?.response?.data?.message || 'Something went wrong', "error");
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h3" mb={2}>Update Project</Typography>

        <TextField
          label="Project Name"
          name="projectName"
          value={formData.projectName}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.projectName}
          helperText={errors.projectName}
        />

        <TextField
          label="Due Date"
          name="dueDate"
          type="date"
          value={formData.dueDate}
          onChange={handleChange}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          error={!!errors.dueDate}
          helperText={errors.dueDate}
          min={new Date().toISOString().split('T')[0]}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
        />

        <TextField
          select
          label="Status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.status}
          helperText={errors.status}
        >
          {statusOptions.map((status) => (
            <MenuItem key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </MenuItem>
          ))}
        </TextField>

        <Box mt={2}>
          <Typography variant="subtitle1" mb={1}>Approve Stages</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.approvals.scriptsAndVisuals
}
                onChange={() => handleCheckboxChange('scriptsAndVisuals')}
              />
            }
            label="Script & Visuals"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.approvals.voiceOver}
                onChange={() => handleCheckboxChange('voiceOver')}
              />
            }
            label="Voice Over"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.approvals.video}
                onChange={() => handleCheckboxChange('video')}
              />
            }
            label="Video"
          />
        </Box>

        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={isSubmitting}>{ isSubmitting ? 'Updating...' : 'Update' }</Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditProjectModal;
